import React, { useState, useEffect } from 'react'
import {
  Filter,
  X,
  ChevronDown,
  Calendar,
  Hash,
  Type,
  Mail,
  LinkIcon,
  CheckSquare,
  Search
} from 'lucide-react'
import MultiSelectFilter from './MultiSelectFilter'

const ColumnFilter = ({
  attribute,
  value,
  operator,
  onFilterChange,
  onClear,
  dataInputId // Add dataInputId prop for fetching unique values
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [filterValue, setFilterValue] = useState(value || '')
  const [filterOperator, setFilterOperator] = useState(operator || 'eq')
  // Separate state for raw text input when using 'in' or 'not_in' operators
  const [rawTextInput, setRawTextInput] = useState('')
  // State for multi-select filter
  const [showMultiSelect, setShowMultiSelect] = useState(false)

  // Initialize rawTextInput when component mounts or value changes
  useEffect(() => {
    if (value && Array.isArray(value) && ['in', 'not_in'].includes(operator)) {
      setRawTextInput(value.join(', '))
    } else if (value && ['in', 'not_in'].includes(operator)) {
      setRawTextInput(String(value))
    }
  }, [value, operator])

  // Get operators based on data type
  const getOperatorsForType = (dataType) => {
    const baseOperators = [
      { value: 'eq', label: 'Equals', symbol: '=' },
      { value: 'ne', label: 'Not equals', symbol: '≠' },
      { value: 'is_null', label: 'Is empty', symbol: '∅' },
      { value: 'is_not_null', label: 'Is not empty', symbol: '≠∅' }
    ]

    switch (dataType) {
      case 'Text':
      case 'Email':
      case 'URL':
        return [
          ...baseOperators,
          { value: 'contains', label: 'Contains', symbol: '⊃' },
          { value: 'not_contains', label: 'Does not contain', symbol: '⊅' },
          { value: 'starts_with', label: 'Starts with', symbol: '⊢' },
          { value: 'ends_with', label: 'Ends with', symbol: '⊣' },
          { value: 'in', label: 'In list', symbol: '∈' },
          { value: 'not_in', label: 'Not in list', symbol: '∉' }
        ]
      
      case 'Number':
      case 'Decimal':
        return [
          ...baseOperators,
          { value: 'gt', label: 'Greater than', symbol: '>' },
          { value: 'gte', label: 'Greater than or equal', symbol: '≥' },
          { value: 'lt', label: 'Less than', symbol: '<' },
          { value: 'lte', label: 'Less than or equal', symbol: '≤' },
          { value: 'between', label: 'Between', symbol: '⟷' },
          { value: 'in', label: 'In list', symbol: '∈' },
          { value: 'not_in', label: 'Not in list', symbol: '∉' }
        ]
      
      case 'Date':
      case 'Datetime':
        return [
          ...baseOperators,
          { value: 'gt', label: 'After', symbol: '>' },
          { value: 'gte', label: 'On or after', symbol: '≥' },
          { value: 'lt', label: 'Before', symbol: '<' },
          { value: 'lte', label: 'On or before', symbol: '≤' },
          { value: 'between', label: 'Between', symbol: '⟷' }
        ]
      
      case 'Boolean':
        return [
          { value: 'eq', label: 'Is', symbol: '=' },
          { value: 'ne', label: 'Is not', symbol: '≠' },
          { value: 'is_null', label: 'Is empty', symbol: '∅' },
          { value: 'is_not_null', label: 'Is not empty', symbol: '≠∅' }
        ]
      
      default:
        return baseOperators
    }
  }

  const operators = getOperatorsForType(attribute.data_type)

  // Get icon for data type
  const getTypeIcon = (dataType) => {
    switch (dataType) {
      case 'Email': return Mail
      case 'URL': return LinkIcon
      case 'Number':
      case 'Decimal': return Hash
      case 'Date':
      case 'Datetime': return Calendar
      case 'Boolean': return CheckSquare
      default: return Type
    }
  }

  const TypeIcon = getTypeIcon(attribute.data_type)

  // Handle filter application
  const applyFilter = () => {
    let valueToApply = filterValue

    // For 'in' and 'not_in' operators, process the raw text input
    if (['in', 'not_in'].includes(filterOperator)) {
      if (!rawTextInput.trim()) {
        return
      }
      // Split by comma, trim whitespace, and filter out empty values
      valueToApply = rawTextInput.split(',').map(v => v.trim()).filter(v => v)
      if (valueToApply.length === 0) {
        return
      }
    } else if (!filterValue && !['is_null', 'is_not_null'].includes(filterOperator)) {
      return
    }

    // Handle date conversion for system columns with display_as_date flag
    let operatorToUse = filterOperator // Use a separate variable to avoid const assignment error

    if (attribute.display_as_date && filterValue && !['is_null', 'is_not_null', 'in', 'not_in'].includes(filterOperator)) {
      if (Array.isArray(filterValue)) {
        // Handle range values [min, max] - for 'between' operator
        valueToApply = filterValue.map((dateVal, index) => {
          if (dateVal) {
            // First value (min) should be start of day, second value (max) should be end of day
            if (index === 0) {
              return `${dateVal}T00:00:00` // Start of day for minimum value
            } else {
              return `${dateVal}T23:59:59` // End of day for maximum value
            }
          }
          return dateVal
        })
      } else {
        // Handle single date value
        if (filterOperator === 'eq') {
          // For date equality on datetime columns, use the backend's date_eq operator
          // which handles the date comparison properly
          operatorToUse = 'date_eq'
          valueToApply = filterValue // Keep as date string (YYYY-MM-DD)
        } else if (filterOperator === 'between') {
          // For date range on datetime columns, use the backend's date_between operator
          operatorToUse = 'date_between'
          // valueToApply should already be an array [startDate, endDate] from the range input
        } else if (filterOperator === 'gt') {
          // For "after" on date columns, use date_after operator
          operatorToUse = 'date_after'
          valueToApply = filterValue // Keep as date string
        } else if (filterOperator === 'lt') {
          // For "before" on date columns, use date_before operator
          operatorToUse = 'date_before'
          valueToApply = filterValue // Keep as date string
        } else if (filterOperator === 'gte') {
          valueToApply = `${filterValue}T00:00:00` // Start of day for >= datetime comparison
        } else if (filterOperator === 'lte') {
          valueToApply = `${filterValue}T23:59:59` // End of day for <= datetime comparison
        }
      }
    }

    onFilterChange({
      column: attribute.name,
      operator: operatorToUse, // Use the potentially modified operator for date columns
      value: valueToApply,
      case_sensitive: false
    })
    setIsOpen(false)
  }

  // Check if column should show multi-select option
  const shouldShowMultiSelectOption = () => {
    return (
      dataInputId && // dataInputId is available
      ['Text', 'Email', 'URL'].includes(attribute.data_type) && // Text-type columns
      !attribute.is_system // Not a system column (system columns handled differently)
      // Allow multi-select for both attributes with and without predefined allowed_values
    )
  }

  // Handle multi-select filter
  const handleMultiSelectChange = (selectedValues) => {
    if (selectedValues.length === 0) {
      // Clear filter if no values selected
      clearFilter()
    } else {
      // Apply 'in' filter with selected values
      onFilterChange({
        column: attribute.name,
        operator: 'in',
        value: selectedValues,
        case_sensitive: false
      })
    }
  }

  // Handle clear filter
  const clearFilter = () => {
    setFilterValue('')
    setFilterOperator('eq')
    setRawTextInput('')
    onClear(attribute.name)
    setIsOpen(false)
  }

  // Render value input based on data type and operator
  const renderValueInput = () => {
    // No input needed for null checks
    if (['is_null', 'is_not_null'].includes(filterOperator)) {
      return null
    }

    // Handle between operator (two inputs)
    if (filterOperator === 'between') {
      const [min, max] = Array.isArray(filterValue) ? filterValue : ['', '']
      
      return (
        <div className="flex space-x-2">
          <input
            type={attribute.data_type === 'Number' || attribute.data_type === 'Decimal' ? 'number' : 
                  attribute.data_type === 'Date' ? 'date' :
                  attribute.data_type === 'Datetime' ? 'datetime-local' : 'text'}
            placeholder="Min"
            value={min}
            onChange={(e) => setFilterValue([e.target.value, max])}
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <input
            type={attribute.data_type === 'Number' || attribute.data_type === 'Decimal' ? 'number' :
                  attribute.data_type === 'Date' || attribute.display_as_date ? 'date' :
                  attribute.data_type === 'Datetime' ? 'datetime-local' : 'text'}
            placeholder="Max"
            value={max}
            onChange={(e) => setFilterValue([min, e.target.value])}
            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
      )
    }

    // Handle list operators (comma-separated values)
    if (['in', 'not_in'].includes(filterOperator)) {
      return (
        <textarea
          placeholder="Enter values separated by commas"
          value={rawTextInput}
          onChange={(e) => setRawTextInput(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          rows={2}
        />
      )
    }

    // Handle boolean type
    if (attribute.data_type === 'Boolean') {
      return (
        <select
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value === 'true')}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select value</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      )
    }

    // Handle allowed values (dropdown)
    if (attribute.allowed_values && attribute.allowed_values.length > 0) {
      return (
        <select
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Select value</option>
          {attribute.allowed_values.map((option, index) => (
            <option key={index} value={option}>
              {option}
            </option>
          ))}
        </select>
      )
    }

    // Default input based on data type
    return (
      <input
        type={attribute.data_type === 'Number' || attribute.data_type === 'Decimal' ? 'number' :
              attribute.data_type === 'Date' || attribute.display_as_date ? 'date' :
              attribute.data_type === 'Datetime' ? 'datetime-local' :
              attribute.data_type === 'Email' ? 'email' :
              attribute.data_type === 'URL' ? 'url' : 'text'}
        placeholder={`Enter ${attribute.label || attribute.name}`}
        value={Array.isArray(filterValue) ? filterValue.join(', ') : (filterValue || '')}
        onChange={(e) => setFilterValue(e.target.value)}
        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
      />
    )
  }

  const hasActiveFilter = value !== undefined && value !== null && value !== ''



  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1 px-2 py-1 text-xs rounded hover:bg-gray-100 ${
          hasActiveFilter ? 'bg-primary-100 text-primary-700' : 'text-gray-500'
        }`}
        title={`Filter ${attribute.label || attribute.name}`}
      >
        <TypeIcon className="h-3 w-3" />
        <Filter className="h-3 w-3" />
        {hasActiveFilter && <span className="w-2 h-2 bg-primary-500 rounded-full" />}
        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <TypeIcon className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-sm text-gray-900">
                  {attribute.label || attribute.name}
                </span>
                <span className="text-xs text-gray-500">
                  ({attribute.data_type})
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              {/* Operator Selection */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Condition
                </label>
                <select
                  value={filterOperator}
                  onChange={(e) => setFilterOperator(e.target.value)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                >
                  {operators.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.symbol} {op.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Value Input */}
              {renderValueInput() && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Value
                  </label>
                  {renderValueInput()}
                </div>
              )}

              {/* Multi-Select Option */}
              {shouldShowMultiSelectOption() && (
                <div className="pt-2 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowMultiSelect(true)
                      setIsOpen(false)
                    }}
                    className="w-full px-3 py-2 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                  >
                    <Search className="h-3 w-3 inline mr-1" />
                    Select from available values
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between pt-2">
                <button
                  onClick={clearFilter}
                  className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                  disabled={!hasActiveFilter}
                >
                  Clear
                </button>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setIsOpen(false)}
                    className="px-3 py-1 text-xs text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={applyFilter}
                    className="px-3 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700"
                    disabled={
                      (['in', 'not_in'].includes(filterOperator) && !rawTextInput.trim()) ||
                      (!['in', 'not_in', 'is_null', 'is_not_null'].includes(filterOperator) &&
                       (filterValue === undefined || filterValue === null || filterValue === '' ||
                        (Array.isArray(filterValue) && filterValue.every(v => !v))))
                    }
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Multi-Select Filter */}
      {showMultiSelect && (
        <MultiSelectFilter
          dataInputId={dataInputId}
          attribute={attribute}
          selectedValues={Array.isArray(value) ? value : []}
          onSelectionChange={handleMultiSelectChange}
          onClose={() => setShowMultiSelect(false)}
        />
      )}
    </div>
  )
}

export default ColumnFilter
